import type { ReactElement } from 'react';

import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

export interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  /** Total number of items (after any filtering) */
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Noun used in the range label, e.g. "runs" */
  itemLabel?: string;
  /** Optional suffix shown after the range, e.g. "(filtered)" */
  suffix?: string;
}

export default function TablePagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  itemLabel = 'cards',
  suffix,
}: TablePaginationProps): ReactElement {
  const rangeStart = Math.min((currentPage - 1) * pageSize + 1, totalItems);
  const rangeEnd = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="bg-slate border-storm flex items-center justify-between border-t px-4 py-2">
      <span className="text-ash text-sm">
        Showing {rangeStart}–{rangeEnd} of {totalItems} {itemLabel}
        {suffix && ` ${suffix}`}
      </span>

      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="hover:bg-storm disabled:text-ash/50 text-ash rounded p-1 transition-colors disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <IoChevronBack className="h-5 w-5" />
          </button>

          <span className="text-silver text-sm">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="hover:bg-storm disabled:text-ash/50 text-ash rounded p-1 transition-colors disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <IoChevronForward className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
